import { useState, useCallback } from "react";
import type { TransactionHash } from "@/types";
import type { Hash, TransactionReceipt, WalletClient } from "viem";
import { useWalletClient } from "wagmi";
import { publicClients } from "@/config";
import { mainnet } from "viem/chains";
import { GENERIC_TRANSACTION_EVENTS, genericTransactionEventEmitter } from "@/events";
import { usePostHogCaptureTx } from "@/posthog/hooks/usePostHogCaptureTx";

interface UseTransactionExecutionProps {
    executeTransactionFn: (walletClient: WalletClient) => Promise<Hash>;
    onSuccess?: (hash: TransactionHash) => void;
    errorMessage?: string;
}

interface TransactionResult {
    cancelled?: boolean;
    receipt?: TransactionReceipt;
}

export function useTransactionExecution({
    executeTransactionFn,
    onSuccess,
    errorMessage = "Transaction failed",
}: UseTransactionExecutionProps) {
    const { wrap } = usePostHogCaptureTx();

    const [transactionHash, setTransactionHash] = useState<Hash>();
    const [isExecuting, setIsExecuting] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [wasCancelled, setWasCancelled] = useState(false);

    const { data: walletClient } = useWalletClient();

    /**
     * Checks if an error is likely a user cancellation
     * @param err The error to check
     * @returns true if the error appears to be a user-initiated cancellation
     */
    const isUserCancellation = (err: any): boolean => {
        if (!err) return false;

        const errorMessage = err.message || err.reason || String(err);
        const lowerCaseMessage = errorMessage.toLowerCase();

        // Common wallet error messages for user cancellation
        return (
            lowerCaseMessage.includes("user denied") ||
            lowerCaseMessage.includes("user rejected") ||
            lowerCaseMessage.includes("canceled by user") ||
            lowerCaseMessage.includes("cancelled by user") ||
            lowerCaseMessage.includes("user cancelled") ||
            lowerCaseMessage.includes("rejected request") ||
            lowerCaseMessage.includes("request rejected") ||
            lowerCaseMessage.includes("action cancelled")
        );
    };

    const executeTransaction = useCallback(async (): Promise<TransactionResult | null> => {
        // Reset state at the beginning of each transaction
        setError(null);
        setIsExecuting(true);
        setWasCancelled(false);

        try {
            if (!walletClient) {
                setError(new Error("Wallet client not available"));
                throw new Error("Wallet client not available");
            }

            try {
                const hash = await executeTransactionFn(walletClient);
                if (!hash) throw new Error("Transaction hash is undefined");

                setTransactionHash(hash);
                const receipt = await wrap(
                    publicClients[mainnet.id].waitForTransactionReceipt({
                        hash,
                        confirmations: 1,
                    }),
                );

                if (receipt.status !== "success") {
                    throw new Error("Transaction not confirmed");
                }

                if (onSuccess) {
                    onSuccess(hash);
                }

                return { receipt };
            } catch (err) {
                // Check if the transaction was cancelled by the user
                if (isUserCancellation(err)) {
                    setWasCancelled(true);
                    return { cancelled: true };
                }

                // Handle other errors
                setError(err instanceof Error ? err : new Error(errorMessage));
                return null;
            }
        } catch (e) {
            // Don't emit failure events for user cancellations
            if (!isUserCancellation(e)) {
                genericTransactionEventEmitter.emit(GENERIC_TRANSACTION_EVENTS.FAILED, {
                    error: `Transaction execution failed: ${e}`,
                });
            }
            throw e;
        } finally {
            setIsExecuting(false);
            return null;
        }
    }, [executeTransactionFn, onSuccess, walletClient, wrap, isUserCancellation]);

    return {
        transactionHash,
        isExecuting,
        error,
        executeTransaction,
        setError,
        wasCancelled,
    };
}
